/*@flow*/
import type { Node } from 'react'; /** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useEffect, Fragment, useState } from 'react';
import type { ValidationErrorT } from 'lib/t/types';
import useCommands from './useCommands';
import useContextState from './useContextState';
import useQuota from './useQuota';
import useInit from './useInit';
import UpgradeCard from './UpgradeCard';
import EmptyArray from 'lib/EmptyArray';
import * as t from 'lib/t';
import { DEFAULT_STROKE_COLOR } from './constants';
import ColorBox from 'comps/ColorBox';
import cx from 'classnames';
import LoadingOverlay from './LoadingOverlay';
import * as I from 'immutable';
import hash from 'lib/hash';
import SaveDistrictBtn from './SaveDistrictBtn';
import NonEmptyString from 'lib/NonEmptyString';
import SavePrimaryAreaIdButton from './SavePrimaryAreaIdButton';

const useDistrictQuota = () => useQuota('District');

const MaxAreaReached = () => (
  <UpgradeCard
    message="You have reached the maximum number of areas allowed by your plan. Please contact us to upgrade."
    emailBody="I'd like to upgrade my account to create more areas."
    data-testid="MaxAreas"
  />
);

const DistrictSchema = t.struct({
  id: t.maybe(t.union([NonEmptyString, t.Number])),
  name: NonEmptyString,
  geojson: NonEmptyString,
  color: t.maybe(NonEmptyString),
});

function EditOverlay(): Node {
  const { areaWorkingCopy, areaBackup } = useContextState();
  const cmd = useCommands();
  const { color, name, id } = areaWorkingCopy;
  const initVe = useInit<ValidationErrorT[]>(
    ['EditOverlay', 'validationErrors'],
    []
  );
  const [validationErrors, setValidationErrors] = useState(initVe);
  const isValid = EmptyArray.is(validationErrors);

  // Backup district on initial load
  useEffect(() => {
    // ...unless there is a backup for this record already.
    if (id === areaBackup.id) return;
    cmd.saveBackup();
  }, []);

  // Validate working copy
  useEffect(() => {
    const value = areaWorkingCopy.toJS();
    const res: { errors: ValidationErrorT[] } = t.validate(
      value,
      DistrictSchema
    );
    const { errors } = res;
    if (hash(errors) !== hash(validationErrors)) {
      setValidationErrors(errors);
    }
  }, [areaWorkingCopy.hashCode()]);

  // Compare the backed up record to the supporting record
  // to find whether record has been mutated.
  const mutated = !I.is(areaWorkingCopy, areaBackup);

  // Compute quota
  const { hasRoom, loading } = useDistrictQuota();

  if (loading) return <LoadingOverlay />;

  const canSave = id ? true : hasRoom;
  const hasValidationError = validationErrors.length > 0;
  if (!canSave) return <MaxAreaReached />;

  return (
    <Fragment>
      <div className="d-flex">
        <label className="text-muted mr-4 mb-0 d-flex pt-2">Name</label>
        <div>
          <input
            type="text"
            className={cx('form-control', { 'is-invalid': hasValidationError })}
            placeholder="Enter name for area"
            value={name || ''}
            data-testid="NameField"
            onChange={(v) => cmd.editName(v.target.value)}
            css={css(`
              min-width: 280px;
              border-radius: 0px;
              padding-left: 0;
              border-left: none;
              border-right: none;
              border-top: none;
              margin: 0;
            `)}
          />
          {hasValidationError && (
            <div className="invalid-feedback">Please choose a name.</div>
          )}
        </div>
      </div>
      <div className="mt-2 mb-0 d-flex">
        <label className="text-muted mr-4 mb-0 d-flex align-items-center">
          Color
        </label>
        <ColorBox
          enabled
          key={color}
          value={color || DEFAULT_STROKE_COLOR}
          onChange={(v) => cmd.editColor(v)}
          boxStyle={`
            margin-bottom: -4px;
            margin-right: 5px;
          `}
          buttonStyle={'margin-top: 1px;'}
          className="EditColorButton btn btn-normalcase pl-1"
        />
      </div>
      <div className="mt-2 mb-0 d-flex">
        <label className="text-muted mr-4 mb-0 d-flex align-items-center">
          Shape
        </label>
        <div className="d-flex">
          <button
            className="btn-link mr-1 pl-0"
            data-testid="EditShapeButton"
            onClick={() => {
              cmd.editPolygon();
            }}
          >
            <i className="fa fa-edit" /> Edit
          </button>
          <button
            className="btn-link"
            onClick={() => cmd.downloadShape()}
            data-testid="DownloadShapeButton"
          >
            <i className="fas fa-file-csv" /> GeoJSON
          </button>
        </div>
      </div>
      <hr />
      {id && <SavePrimaryAreaIdButton id={id} />}
      <hr />
      <div className="mt-2 mb-0 d-flex">
        <SaveDistrictBtn disabled={!(mutated && canSave && isValid)} />
      </div>
      {!hasRoom && (
        <Fragment>
          <hr />
          <MaxAreaReached />
        </Fragment>
      )}
    </Fragment>
  );
}

export default EditOverlay;
